import { Breakpoint, Theme } from '@mui/material';

/**
 * Helper utilities for CSS container queries.
 *
 * Container queries allow styles to respond to the container's width rather than
 * the viewport width. This is useful for truly responsive components that adapt
 * to their available space regardless of where they're placed.
 *
 * NOTE: MUI v7+ has native container query support via `theme.containerQueries`.
 * When upgrading to MUI v7, replace usage of these helpers with the native API.
 * See: https://mui.com/material-ui/customization/container-queries/
 *
 * @example
 * // In a styled component:
 * const ResponsiveBox = styled(Box)(({ theme }) => ({
 *   containerType: 'inline-size',
 *   [containerQuery(theme).down(840)]: {
 *     flexDirection: 'column',
 *   },
 * }));
 *
 * @example
 * // In sx prop:
 * <Box sx={{
 *   containerType: 'inline-size',
 *   [containerQuery(theme).down('md')]: {
 *     flexDirection: 'column',
 *   },
 * }} />
 */

/**
 * Converts an MUI media query to a container query.
 * MUI generates: `@media (max-width: 840px)`
 * This converts to: `@container (max-width: 840px)`
 */
function toContainerQuery(mediaQuery: string): string {
  return mediaQuery.replace('@media', '@container');
}

/**
 * Creates container query helpers that mirror MUI's breakpoint API.
 *
 * @param theme - MUI theme object
 * @returns Object with breakpoint methods that return container query strings
 */
export function containerQuery(theme: Theme) {
  return {
    /**
     * Styles apply when container width is >= the breakpoint value.
     * @param key - Breakpoint key ('xs', 'sm', 'md', 'lg', 'xl') or pixel value
     */
    up: (key: Breakpoint | number) =>
      toContainerQuery(theme.breakpoints.up(key)),

    /**
     * Styles apply when container width is < the breakpoint value.
     * @param key - Breakpoint key ('xs', 'sm', 'md', 'lg', 'xl') or pixel value
     */
    down: (key: Breakpoint | number) =>
      toContainerQuery(theme.breakpoints.down(key)),

    /**
     * Styles apply when container width is between two breakpoint values.
     * @param start - Start breakpoint (inclusive)
     * @param end - End breakpoint (exclusive)
     */
    between: (start: Breakpoint | number, end: Breakpoint | number) =>
      toContainerQuery(theme.breakpoints.between(start, end)),

    /**
     * Styles apply only at the specified breakpoint range.
     * @param key - Breakpoint key ('xs', 'sm', 'md', 'lg', 'xl')
     */
    only: (key: Breakpoint) => toContainerQuery(theme.breakpoints.only(key)),
  };
}

/**
 * Common container query breakpoints used across the app.
 * Add new breakpoints here as needed.
 */
export const CONTAINER_BREAKPOINTS = {
  /** Breakpoint for responsive accordion/card layouts (actions stack below) */
  RESPONSIVE_STACK: 840,
} as const;
